
import { useEffect } from 'react';

export const useDropdownStyles = (isLoaded: boolean) => {
  // Add observer to fix autocomplete dropdown styles when DOM changes
  useEffect(() => {
    if (!isLoaded) return;

    // Fix CSS for Google Maps autocomplete dropdown
    const fixAutocompleteStyles = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        if (container instanceof HTMLElement) {
          // Ensure high z-index, proper positioning and no pointer events on parent elements
          container.style.zIndex = '9999';
          container.style.position = 'fixed';
          
          // Fix click events by ensuring pointer-events are auto
          container.style.pointerEvents = 'auto';
          
          // Make sure the dropdown is visible
          container.style.display = 'block';
          container.style.visibility = 'visible';
          
          // Add some margin to dropdown
          container.style.marginTop = '2px';
        }
      });
      
      // Make sure the overlay doesn't block clicks
      const overlays = document.querySelectorAll('.pac-container ~ div');
      overlays.forEach(overlay => {
        if (overlay instanceof HTMLElement) {
          overlay.style.pointerEvents = 'none';
        }
      });
    };

    // Function to clean up Google autocomplete dropdown - we'll make this more aggressive
    const cleanupAutocomplete = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        if (container instanceof HTMLElement) {
          container.style.display = 'none';
          container.style.visibility = 'hidden';
          container.style.pointerEvents = 'none'; // Ensure it's not clickable
          
          // Remove container completely from DOM if persisting
          if (container.parentNode) {
            setTimeout(() => {
              if (document.body.contains(container)) {
                document.body.removeChild(container);
              }
            }, 200);
          }
        }
      });
      
      // Also hide any remaining "Powered by Google" elements
      const poweredByGoogle = document.querySelectorAll('.pac-logo:after');
      poweredByGoogle.forEach(element => {
        if (element instanceof HTMLElement) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
        }
      });
    };

    // Listen for dialog/modal close events
    const handleDialogClose = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.getAttribute('data-state') === 'closed' && 
          (target.getAttribute('role') === 'dialog' || target.closest('[role="dialog"]'))) {
        cleanupAutocomplete();
      }
    };

    // Create observer to watch for DOM changes and fix styles
    const observer = new MutationObserver(() => {
      fixAutocompleteStyles();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    
    // Run immediately once and then again after a delay to catch async rendering
    fixAutocompleteStyles();
    setTimeout(fixAutocompleteStyles, 300);
    
    // Also run when user interacts with the input
    const handleInteraction = () => {
      setTimeout(fixAutocompleteStyles, 100);
    };
    
    // Add event listeners
    document.addEventListener('click', handleInteraction);
    document.addEventListener('focus', handleInteraction, true);
    document.addEventListener('animationend', handleDialogClose);
    
    // Handle item selection in dropdown
    const handleSelectItem = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('pac-item') || target.closest('.pac-item')) {
        // When an item in the dropdown is clicked
        setTimeout(cleanupAutocomplete, 50);
      }
    };
    document.addEventListener('click', handleSelectItem, true);
    
    // Handle ESC key to close the dropdown
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanupAutocomplete();
      } else if (e.key === 'Enter') {
        // When Enter is pressed to select an autocomplete item
        setTimeout(cleanupAutocomplete, 50);
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('click', handleSelectItem, true);
      document.removeEventListener('focus', handleInteraction, true);
      document.removeEventListener('animationend', handleDialogClose);
      document.removeEventListener('keydown', handleKeyDown);
      cleanupAutocomplete();
    };
  }, [isLoaded]);
};
